// src/api/cartApi.js
//
// REST cart API — https://orbiee-backend-dev-218142584790.us-central1.run.app/docs#/cart
//
import { CART_API_URL } from "../config/apiConfig";
import { getUserProfile } from "../utils/auth";
import { getOrbieeDeviceId } from "../services/websocket";

async function buildCartHeaders() {
  const profile = getUserProfile();
  const customerId = profile?.customer_id;
  if (!customerId) return null;

  const deviceId = await getOrbieeDeviceId();
  return {
    "Content-Type": "application/json",
    "X-User-Id": String(customerId),
    "X-Device-Id": String(deviceId),
  };
}

function optionalSize(size) {
  if (size == null || size === "") return undefined;
  return String(size);
}

async function parseCartResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail =
      typeof data.detail === "string"
        ? data.detail
        : data.message || data.error?.message || `Cart request failed (${response.status})`;
    throw new Error(detail);
  }
  if (data.success === false) {
    throw new Error(data.message || "Cart request failed");
  }
  return data;
}

/**
 * GET /api/cart — fetch the shopper's current cart.
 */
export async function fetchCart() {
  const headers = await buildCartHeaders();
  if (!headers) return null;

  const response = await fetch(CART_API_URL, {
    method: "GET",
    headers,
  });
  return parseCartResponse(response);
}

/**
 * POST /api/cart — add units (merges when same product+size exists).
 */
export async function addCartLine({ article_id, quantity = 1, size }) {
  const headers = await buildCartHeaders();
  if (!headers) return null;

  const body = {
    article_id: String(article_id),
    quantity: Math.max(1, Math.min(99, Math.floor(Number(quantity) || 1))),
  };
  const sizeVal = optionalSize(size);
  if (sizeVal !== undefined) body.size = sizeVal;

  const response = await fetch(CART_API_URL, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  return parseCartResponse(response);
}

/**
 * PUT /api/cart — set absolute line quantity (0 removes the line).
 */
export async function updateCartLine({ article_id, quantity, size }) {
  const headers = await buildCartHeaders();
  if (!headers) return null;

  const body = {
    article_id: String(article_id),
    quantity: Math.max(0, Math.min(99, Math.floor(Number(quantity) || 0))),
  };
  const sizeVal = optionalSize(size);
  if (sizeVal !== undefined) body.size = sizeVal;

  const response = await fetch(CART_API_URL, {
    method: "PUT",
    headers,
    body: JSON.stringify(body),
  });
  return parseCartResponse(response);
}

/**
 * DELETE /api/cart — remove one line (entire line when quantity omitted).
 */
export async function removeCartLine({ article_id, size }) {
  const headers = await buildCartHeaders();
  if (!headers) return null;

  const body = { article_id: String(article_id) };
  const sizeVal = optionalSize(size);
  if (sizeVal !== undefined) body.size = sizeVal;

  const response = await fetch(CART_API_URL, {
    method: "DELETE",
    headers,
    body: JSON.stringify(body),
  });
  return parseCartResponse(response);
}

/**
 * DELETE /api/cart?all=true — empty the entire cart.
 */
export async function clearCartRemote() {
  const headers = await buildCartHeaders();
  if (!headers) return null;

  const response = await fetch(`${CART_API_URL}?all=true`, {
    method: "DELETE",
    headers,
  });
  return parseCartResponse(response);
}
